import errorHandler from "common/errorHandler";
import { showError, showSuccess } from "common/ToastNotifications";
import _ from "lodash";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { Endpoints } from "services/api";
import * as actions from "./actions";

// --- API Calls ---
const successOn2xx = {
    validateStatus: (status) => status >= 200 && status < 300,
};

const getUser = (state) => state.userLogin.user;

function* addComment(api, recordId, commentData) {
    const result = yield call([api, api.post], `${Endpoints.comments}/api/v1/record/${recordId}`, commentData, {}, successOn2xx);
    return result.data;
}

function* updateComment(api, recordId, commentId, commentData) {
    const result = yield call([api, api.put], `${Endpoints.comments}/api/v1/record/${recordId}/comment/${commentId}`, commentData, {}, successOn2xx);
    return result.data;
}

function* deleteComment(api, recordId, commentId) {
    yield call([api, api.delete], `${Endpoints.comments}/api/v1/record/${recordId}/comment/${commentId}`, null, {}, successOn2xx);
    return commentId;
}

function* getComments(api, recordId) {
    const result = yield call([api, api.get], `${Endpoints.comments}/api/v1/record/${recordId}`);
    return result.data;
}

function* getCommentsPermission(api, userId) {
    let result = yield call([api, api.get], `${Endpoints.permissions}/api/v1/users/${userId}/permissions/resources/comments`);
    return result.data;
}

// --- Sagas ---
export function* getCommentsRequestSaga(api, action) {
    try {
        const recordId = action.payload;
        const comments = yield call(getComments, api, recordId);
        yield put(actions.getCommentsSuccess(recordId, comments));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "comments");
        yield put(actions.getCommentsFailure(errorObject));
    }
}

export function* addCommentRequestSaga(api, action) {
    const { recordId, commentData } = action.payload;
    try {
        const newComment = yield call(addComment, api, recordId, commentData);
        yield put(actions.addCommentSuccess(recordId, newComment));
        showSuccess("Comment added successfully");
    } catch (error) {
        let errorObject = errorHandler(error, "Add", "comment");
        showError("Failed to add comment");

        yield put(actions.addCommentFailure(errorObject));
    }
}

export function* updateCommentRequestSaga(api, action) {
    const { recordId, commentId, commentData } = action.payload;
    try {
        const updatedComment = yield call(updateComment, api, recordId, commentId, commentData);
        yield put(actions.updateCommentSuccess(recordId, updatedComment));
        showSuccess("Comment updated successfully");
    } catch (error) {
        let errorObject = errorHandler(error, "Update", "comment");
        showError("Failed to update comment");

        yield put(actions.updateCommentFailure(errorObject));
    }
}

export function* deleteCommentRequestSaga(api, action) {
    const { recordId, commentId } = action.payload;
    try {
        yield call(deleteComment, api, recordId, commentId);
        yield put(actions.deleteCommentSuccess(recordId, commentId));
        showSuccess("Comment deleted successfully");
    } catch (error) {
        let errorObject = errorHandler(error, "Delete", "comment");
        showError("Failed to delete comment");

        yield put(actions.deleteCommentFailure(errorObject));
    }
}

export function* getCommentsPermissionRequestSaga(api, action) {
    try {
        let permission = { canAdd: false, canView: false, canEdit: false, canDelete: false };
        let user = yield select(getUser);
        const permissionResponse = yield call(getCommentsPermission, api, user.userId);
        if (permissionResponse && permissionResponse.Items && permissionResponse.Items.length > 0) {
            let permissionActions = permissionResponse.Items.map((item) => item.actions);
            permissionActions = _.union(...permissionActions);
            if (permissionActions.length > 0) {
                if (permissionActions.includes("add")) {
                    permission.canAdd = true;
                }
                if (permissionActions.includes("view")) {
                    permission.canView = true;
                }
                if (permissionActions.includes("edit")) {
                    permission.canEdit = true;
                }
                if (permissionActions.includes("delete")) {
                    permission.canDelete = true;
                }
            }
        }
        yield put(actions.getCommentsPermissionSuccess(permission));
    } catch (error) {
        let errorObject = errorHandler(error, "Get", "comments permissions");
        yield put(actions.getCommentsPermissionFailure(errorObject));
    }
}

// --- Watchers ---
export default function* ({ api }) {
    yield takeEvery(actions.GET_COMMENTS_REQUEST, getCommentsRequestSaga, api);
    yield takeEvery(actions.ADD_COMMENT_REQUEST, addCommentRequestSaga, api);
    yield takeEvery(actions.UPDATE_COMMENT_REQUEST, updateCommentRequestSaga, api);
    yield takeEvery(actions.DELETE_COMMENT_REQUEST, deleteCommentRequestSaga, api);
    yield takeEvery(actions.GET_COMMENTS_PERMISSION_REQUEST, getCommentsPermissionRequestSaga, api);
}
